/**
 * AssignmentAnalytics — Phase 11 Sprint 11-S7
 * Auto-assignment performance panel for the Analytics tab.
 *
 * Layout:
 *   4 metric cards (2×2 grid): auto-assign rate, avg confidence,
 *                               reassignment rate, total requests
 *   Healer load table: Healer | Active | Completed (sorted by active desc)
 *
 * All colors from colors.js — no hardcoded hex.
 */

import React, { useState, useEffect } from 'react';
import colors from '../../styles/colors';
import MetricCard from './MetricCard';
import { getAssignmentAnalytics } from '../../utils/api';

export default function AssignmentAnalytics() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getAssignmentAnalytics();
        setData(res.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted, fontSize: 14 }}>
        Loading analytics…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding:      16,
        borderRadius: 8,
        margin:       24,
        background:   colors.statusDangerLight,
        border:       `1px solid ${colors.statusDanger}`,
        color:        colors.statusDanger,
        fontSize:     14,
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{
        fontSize:    20,
        fontWeight:  700,
        color:       colors.textPrimary,
        marginBottom: 4,
        fontFamily:  "'Raleway', sans-serif",
      }}>
        Assignment Analytics
      </div>
      <div style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 24 }}>
        Auto-assignment performance for this centre
      </div>

      {/* 2×2 metric grid */}
      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap:                 16,
        marginBottom:        28,
      }}>
        <MetricCard
          label="Auto-assign rate"
          value={`${data.autoAssignRate}%`}
          sub={`${data.autoAssignedCount} of ${data.totalRequests} requests`}
          accent={colors.brandPrimary}
        />
        <MetricCard
          label="Avg confidence score"
          value={data.avgConfidence > 0 ? String(data.avgConfidence) : '—'}
          sub="Average score of auto-assigned requests"
          accent={colors.brandSecondary}
        />
        <MetricCard
          label="Reassignment rate"
          value={`${data.reassignmentRate}%`}
          sub={`${data.humanAssigned} human-assigned total`}
          accent={colors.statusWarning}
        />
        <MetricCard
          label="Total requests"
          value={String(data.totalRequests)}
          sub="Excludes cancelled"
          accent={colors.brandBlue}
        />
      </div>

      {/* Healer load table */}
      <div style={{
        background:   colors.backgroundCard,
        border:       `1px solid ${colors.borderDefault}`,
        borderRadius: 8,
        overflow:     'hidden',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.borderDefault}` }}>
          <div style={{
            fontSize:   15,
            fontWeight: 700,
            color:      colors.textPrimary,
            fontFamily: "'Raleway', sans-serif",
          }}>
            Healer Load
          </div>
          <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
            Active and completed cases per healer — sorted by active count
          </div>
        </div>

        {data.healerLoad.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
            No healer assignment data yet
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundSecondary }}>
                {['Healer', 'Active', 'Completed'].map(function(h) {
                  return (
                    <th key={h} style={{
                      padding:       '10px 20px',
                      textAlign:     h === 'Healer' ? 'left' : 'center',
                      fontSize:      12,
                      fontWeight:    700,
                      color:         colors.textSecondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      {h}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.healerLoad.map(function(row, i) {
                return (
                  <tr
                    key={String(row.healerId)}
                    style={{
                      borderTop:  `1px solid ${colors.borderDefault}`,
                      background: i % 2 === 0 ? colors.backgroundCard : colors.backgroundPage,
                    }}
                  >
                    <td style={{
                      padding:    '12px 20px',
                      fontSize:   14,
                      fontWeight: 600,
                      color:      colors.textPrimary,
                    }}>
                      {row.healerName}
                    </td>
                    <td style={{ padding: '12px 20px', fontSize: 14, textAlign: 'center' }}>
                      <span style={{
                        display:      'inline-block',
                        minWidth:     32,
                        padding:      '2px 10px',
                        borderRadius: 12,
                        fontWeight:   700,
                        fontSize:     13,
                        background:   row.activeCount > 0 ? colors.brandPrimaryLight : colors.backgroundSecondary,
                        color:        row.activeCount > 0 ? colors.brandPrimaryDark  : colors.textMuted,
                      }}>
                        {row.activeCount}
                      </span>
                    </td>
                    <td style={{
                      padding:   '12px 20px',
                      fontSize:  14,
                      textAlign: 'center',
                      color:     colors.textSecondary,
                    }}>
                      {row.completedCount}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
